/**
 * Marking an answer in the browser (offline-first sessions, 9 Oct 2026).
 *
 * A copy of `mark`, `edit_distance` and `normalise_text` in
 * backend/wordsprout/services/scoring.py, so the child sees right or wrong
 * the moment they answer, with or without a connection. The server marks
 * the answer again when it arrives, and its mark is the one recorded.
 *
 * Two copies of a rule can drift apart. They are held together by one file
 * of cases, `marking.cases.json`, which both test suites check:
 * backend/tests/test_offline_session.py and marking.test.ts. Change a rule
 * here, change it there, and add a case.
 */

/** The question's payload with its key put back: options, answer, answer_index. */
export interface Marked {
  options?: unknown[]
  answer?: unknown
  answer_index?: number | null
}

const UNMARKED = new Set(['learning', 'writing_own', 'joke_lab', 'comic_caption', 'boss_battle', 'character_chat'])
const RECALL = new Set(['cued_recall', 'free_recall'])
const CHOICE = new Set(['cloze', 'definition', 'analogy', 'collocation', 'word_ladder', 'odd_one_out',
  'synonym', 'antonym', 'reading'])

/** Case, spaces, and curly apostrophes (an iPad types ’) do not count. */
export function normaliseText(value: unknown): string {
  return String(value ?? '').replace(/[‘’]/g, "'").trim().toLowerCase()
    .split(/\s+/).filter(Boolean).join(' ')
}

/** Python's str.isdigit, for the strings an answer can be. */
const isDigits = (s: string) => /^[0-9]+$/.test(s)

/** Is this answer right? null for a trial that is not marked at all. */
export function mark(itemType: string, key: Marked, response: unknown): boolean | null {
  if (UNMARKED.has(itemType)) return null

  if (RECALL.has(itemType)) return normaliseText(response) === normaliseText(key.answer)

  if (CHOICE.has(itemType)) {
    if (typeof response === 'number' || (typeof response === 'string' && isDigits(response))) {
      const index = Number(response)
      return index >= 0 && index < (key.options ?? []).length && index === key.answer_index
    }
    return normaliseText(response) === normaliseText(key.answer)
  }

  if (itemType === 'double_blank') {
    // One pair, both halves, in order. Half right is wrong.
    if (typeof response === 'number' || (typeof response === 'string' && isDigits(response))) {
      return Number(response) === key.answer_index
    }
    const want = Array.isArray(key.answer) ? key.answer.map(normaliseText) : []
    const got = Array.isArray(response) ? response.map(normaliseText) : []
    return want.length > 0 && want.length === got.length && want.every((w, i) => w === got[i])
  }

  return null
}

/** Levenshtein, to tell a near miss ('aple') from a wild guess. */
export function editDistance(a: string, b: string): number {
  const x = normaliseText(a)
  const y = normaliseText(b)
  if (x === y) return 0
  if (!x) return y.length
  if (!y) return x.length
  let previous = Array.from({ length: y.length + 1 }, (_, i) => i)
  for (let i = 1; i <= x.length; i++) {
    const current = [i]
    for (let j = 1; j <= y.length; j++) {
      current.push(Math.min(
        previous[j]! + 1,
        current[j - 1]! + 1,
        previous[j - 1]! + (x[i - 1] === y[j - 1] ? 0 : 1),
      ))
    }
    previous = current
  }
  return previous[y.length]!
}
